import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Flex,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from "@chakra-ui/react";
import { formatAmountInINR } from "jsFunctions/formatAmountInINR";
import dateTimeFormat from "jsFunctions/dateTimeFormat";
import { useDispatch, useSelector } from "react-redux";
import "./paymentReceiptPdf.css";
import { IoClose } from "react-icons/io5";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { FaFileDownload } from "react-icons/fa";
import { formatAmountIndianRupees } from "jsFunctions/formatAmountInINR";
import { convertAmountToWords } from "./helper";
import SpinnerLoader from "components/Spinner/SpinnerLoader";
import { useToasts } from "react-toast-notifications";

import GetAddress from "components/GetAddress";
import { calculateTotalCredit } from "jsFunctions/helperFunctions";
import { poNumberFormate } from "jsFunctions/helperFunctions";
import { fetchAsyncGetClientByID } from "redux/clientProjectSlice";
import { pmtNumberFormate } from "jsFunctions/helperFunctions";
import { allPaymentMode } from "components/allForm/PurchaseItem/selectedOptions";

const PaymentReceiptPdf = (props) => {
  const { isOpen, onClose, expenseData,currency, paymentData, autoCount, expenseType } =
    props;
  const organizationList = useSelector((state) => state.organizationData);
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const [clientDetail, setClientDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const logoData = useSelector((state) => state.logoData);
  const logoUrl = logoData?.downloadUrl || "";

  const fetchClientDetails = useCallback(
    async (clientId) => {
      setLoading(true);
      try {
        const res = await dispatch(fetchAsyncGetClientByID(clientId));
        if (res?.payload) {
          setClientDetail(res.payload.client);
        }
      } catch (error) {
        addToast(`Error fetching vendor details: ${error.message}`, {
          appearance: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [dispatch, addToast]
  );

  useEffect(() => {
    if (expenseData) {
      const clientId = expenseData?.clId._id;
      if (clientId) {
        fetchClientDetails(clientId);
      }
    }
  }, [expenseData, fetchClientDetails]);

  const gstData = useMemo(() => {
    return expenseData?.gstRc || [];
  }, [expenseData]);

  const tdsData = useMemo(() => {
    if (expenseData?.tdsApl?.sts) {
      return expenseData.tdsApl.tdsRc.map((item) => ({
        totalAmount: item?.bsAmt,
        tdsPercent: item?.prc,
        totalTDS: item?.tdsAmt,
      }));
    }
    return null;
  }, [expenseData]);

  const historyData = useMemo(() => {
    if (expenseData?.pmtRc) {
      return expenseData.pmtRc.filter((_, index) => index !== autoCount);
    }
    return [];
  }, [expenseData, autoCount]);

  const organizationData = useMemo(() => {
    if (organizationList?.length) {
      return organizationList[0];
    }
  }, [organizationList]);

  const calculateTotalPaymentAmount = useCallback(() => {
    return paymentData?.amt || 0;
  }, [paymentData]);

  const calculateTotalGst = useCallback(() => {
    return gstData.reduce((total, item) => {
      const gstAmount = item?.amt || {};
      return (
        total +
        (parseFloat(gstAmount?.s || 0) +
          parseFloat(gstAmount?.i || 0) +
          parseFloat(gstAmount?.c || 0))
      );
    }, 0);
  }, [gstData]);

  const calculateTotalTDS = useCallback(() => {
    return tdsData.reduce((total, item) => total + (item.totalTDS || 0), 0);
  }, [tdsData]);

  const calculateTotalPaymentHistory = useCallback(() => {
    return historyData.reduce((total, item) => total + (item?.amt || 0), 0);
  }, [historyData]);

  const calculateCreditAmt = useCallback(() => {
    if(expenseData?.crApl?.sts && expenseData?.crApl?.list?.length){
      const amount = calculateTotalCredit(expenseData?.crApl?.list);
      return amount
    }
    return 0;
  }, [expenseData]);

  const creditData = useMemo(() => {
    if (expenseData?.crApl?.list) {
      return expenseData.crApl.list;
    }
    return [];
  }, [expenseData]);

  const handlePdf = async () => {
    setLoading(true);
    const paymentId = pmtNumberFormate(paymentData?.pmtPrfx);

    const pdf = new jsPDF();

    try {
      const renderSection = async (section) => {
        const canvas = await html2canvas(section, {
          useCORS: true,
          allowTaint: true,
          scale: 6,
        });
        return canvas;
      };

      const [section1Canvas, section2Canvas] = await Promise.all([
        renderSection(document.getElementById("payment-mode")),
        renderSection(document.getElementById("payment-for")),
      ]);

      const imgData1 = section1Canvas.toDataURL("image/jpeg",1);
      const imgData2 = section2Canvas.toDataURL("image/jpeg",1);

      const imgWidth = 190;
      const pageHeight = pdf.internal.pageSize.height;

      const imgHeight1 =
        (section1Canvas.height * imgWidth) / section1Canvas.width;
      const imgHeight2 =
        (section2Canvas.height * imgWidth) / section2Canvas.width;

      let position = 10;

      pdf.addImage(imgData1, "PNG", 10, position, imgWidth, imgHeight1);
      let heightLeft = imgHeight1 - pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight1;
        pdf.addPage();
        pdf.addImage(imgData1, "PNG", 10, position, imgWidth, imgHeight1);
        heightLeft -= pageHeight;
      }

      pdf.addPage();
      position = 10;
      pdf.addImage(imgData2, "PNG", 10, position, imgWidth, imgHeight2);
      heightLeft = imgHeight2 - pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight2;
        pdf.addPage();
        pdf.addImage(imgData2, "PNG", 10, position, imgWidth, imgHeight2);
        heightLeft -= pageHeight;
      }

      const pdfBlob = pdf.output("blob");
      const url = URL.createObjectURL(pdfBlob);

      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${paymentId}.pdf`; 
      
      document.body.appendChild(a);
      a.click();
      
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setLoading(false);
      }, 100);
    } catch (error) {
      setLoading(false);
    }
  };

  const getPaymentMode=(id)=>{
    const payMod = allPaymentMode.find(opt=> opt.id === id);
    return payMod?.value || ""

  }
  return (
    <Modal isOpen={isOpen} onClose={onClose} className="modal-main" size="xl">
      <ModalOverlay />
      <ModalContent
        className="add-form-modal-content"
        top="0"
        margin="0 auto"
        bottom="0"
      >
        <div className="fixed-header  mt-0">
          <Box className="payment-template">
            <Tooltip
              label="Close"
              placement="bottom"
              hasArrow
              className="common-tooltip"
            >
              <button className="close-btn" onClick={onClose}>
                <IoClose />
              </button>
            </Tooltip>
            <Tooltip
              label="Download"
              placement="bottom"
              hasArrow
              className="common-tooltip"
            >
              <button onClick={handlePdf} className="pdf-downlode-icon">
                <FaFileDownload />
              </button>
            </Tooltip>
          </Box>
        </div>
        <ModalBody>
          <>
            <Box className="payment-mode-details">
              {loading ? (
                <SpinnerLoader />
              ) : (
                <>
                  <Box id="payment-mode" className="main-template">
                    <div className="details-container">
                      <Box className="ledger-template">
                        <Flex
                          align="center"
                          direction="column"
                          className="ledger-logo"
                        >
                          <img src={logoUrl} alt="logo" />
                        </Flex>
                        <Box
                          display="flex"
                          className="ledger-header"
                          justifyContent="space-between"
                        >
                          <div className="header-left header-box">
                            <Text className="title mt-3">
                              {organizationData?.name}
                            </Text>
                            <Text className="address">
                              <GetAddress address={organizationData?.add} />
                            </Text>
                            <Text className="address">
                              <span>Company ID :</span>
                              <span> {organizationData?.cpId}</span>
                            </Text>
                            <Text className="address">
                              <span> GST :</span>
                              <span> {organizationData?.gstNo}</span>
                            </Text>
                          </div>
                          <div className="header-right header-box">
                        <Text className="label paid-to">Bill To</Text>

                        <Text className="title">{clientDetail?.clName}</Text>
                        <Text className="address">
                          <GetAddress address={clientDetail?.add} />
                        </Text>
                        <Text className="address">
                          <span>GST :</span>
                          <span>
                            {" "}
                            {clientDetail?.gsts[0]?.gstNo
                              ? clientDetail?.gsts[0]?.gstNo
                              : "NA"}
                          </span>
                        </Text>
                      </div>
                        </Box>
                      </Box>
                    </div>
                    <Box className="payment-mads">
                      <Text>
                        <strong>Payment Receipt</strong>
                      </Text>
                    </Box>
                    <Box>
                      <Box className="payment-details">
                        <div className="payment-text-div">
                          <div className="text1">Payment#</div>
                          <div className="text2">
                            {pmtNumberFormate(paymentData?.pmtPrfx)}
                          </div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Payment Date</div>
                          <div className="text2">
                            {dateTimeFormat(paymentData?.dt)}
                          </div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Reference Number</div>
                          <div className="text2">
                            {paymentData?.ref ? paymentData?.ref : "-"}
                          </div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Payment Mode</div>
                          <div className="text2">
                            {paymentData?.paymentMode
                              ? paymentData?.paymentMode?.value
                              : "-"}
                          </div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Paid To</div>
                          <div className="text2">{clientDetail?.clName}</div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Place Of Supply</div>
                          <div className="text2">
                            {clientDetail?.gsts && clientDetail?.gsts?.length
                              ? `${clientDetail?.gsts[0]?.pos} (${clientDetail?.gsts[0]?.gstNo})`
                              : "-"}
                          </div>
                        </div>

                        <div className="payment-text-div">
                          <div className="text1">Amount Received</div>
                          <div className="text2">
                            {formatAmountIndianRupees(paymentData?.amt || 0)}
                            /-
                          </div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Amount Received In Words</div>
                          <div className="text2">
                            <span>{currency?.name || "Indian Rupee"} </span>
                            {convertAmountToWords(paymentData?.amt || 0)}{" "}
                          </div>
                        </div>
                      </Box>
                      <Box className="amount-div">
                        <span>Amount Received</span>
                        <br />
                        <span className="pcs-total">
                          {currency?.symbol || "₹"}{" "}
                          {formatAmountIndianRupees(paymentData?.amt || 0)}
                        </span>
                      </Box>
                    </Box>
                    <div className="clear"></div>
                  </Box>
                  <Box id="payment-for" className="main-template">
                    <div className="payment-table-box mt-5">
                      <h4 className="heading">Payment Information</h4>
                      <Table>
                        <Thead>
                          <Tr>
                            <Th className="width_150">Payment Id</Th>
                            <Th>Payment Date</Th>
                            <Th>Payment Mode</Th>
                            <Th>Amount Received</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            <Td>{pmtNumberFormate(paymentData?.pmtPrfx)}</Td>
                            <Td>{dateTimeFormat(paymentData?.dt)}</Td>
                            <Td>{paymentData?.paymentMode?.value}</Td>
                            <Td>
                              {currency?.symbol || "₹"}{" "}
                              {formatAmountIndianRupees(paymentData?.amt || 0)}
                            </Td>
                          </Tr>
                          <Tr>
                            <Td colSpan={3} className="f-600">
                              Total Amount :
                            </Td>
                            <Td className="f-600">
                            <span>{currency?.symbol || "₹"} </span>
                              {formatAmountIndianRupees(
                                calculateTotalPaymentAmount()
                              )}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </div>
                    {gstData && gstData?.length > 0 && (
                      <div className="payment-table-box">
                        <h4 className="heading">GST Information</h4>
                        <Table>
                          <Thead>
                            <Tr>
                              {/* <Th>No.</Th> */}
                              <Th className="width_150">Taxable Amount</Th>
                              <Th>IGST</Th>
                              <Th>SGST</Th>
                              <Th>CGST</Th>
                              <Th>Total GST</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {gstData.map((item, index) => {
                              const { bsAmt, amt } = item;
                              const totalGst =
                                parseFloat(amt?.s || 0) +
                                parseFloat(amt?.i || 0) +
                                parseFloat(amt?.c || 0);
                              return (
                                <Tr key={index}>
                                  <Td>
                                    {bsAmt
                                      ? formatAmountInINR(Number(bsAmt || 0))
                                      : Number(0).toFixed(2)}
                                  </Td>

                                  <Td>
                                    <span className="rupee-symbol">₹</span>{" "}
                                    {amt?.i
                                      ? formatAmountIndianRupees(Number(amt?.i))
                                      : Number(0).toFixed(2)}
                                  </Td>
                                  <Td>
                                    <span className="rupee-symbol">₹</span>{" "}
                                    {amt?.s
                                      ? formatAmountIndianRupees(Number(amt?.s))
                                      : Number(0).toFixed(2)}
                                  </Td>
                                  <Td>
                                    <span className="rupee-symbol">₹</span>{" "}
                                    {amt?.c
                                      ? formatAmountIndianRupees(Number(amt?.c))
                                      : Number(0).toFixed(2)}
                                  </Td>
                                  <Td>
                                    <span className="rupee-symbol">₹</span>{" "}
                                    {totalGst
                                      ? formatAmountIndianRupees(
                                          Number(totalGst)
                                        )
                                      : Number(0).toFixed(2)}
                                  </Td>
                                </Tr>
                              );
                            })}
                            <Tr>
                              <Td className="f-600" colSpan={4}>
                                Total GST:
                              </Td>
                              <Td className="f-600">
                                {" "}
                                {formatAmountInINR(calculateTotalGst())}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </div>
                    )}
                    {tdsData && tdsData?.length > 0 && (
                      <div className="payment-table-box">
                        <h4 className="heading">TDS Information</h4>
                        <Table>
                          <Thead>
                            <Tr>
                              <Th>Taxable Amount</Th>
                              <Th>TDS</Th>
                              <Th>TDS Amount</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {tdsData.map((item, index) => {
                              return (
                                <Tr>
                                  <Td>
                                    {currency?.symbol || "₹"}{" "}
                                    {formatAmountIndianRupees(
                                      item?.totalAmount || 0
                                    )}
                                  </Td>
                                  <Td>{item?.tdsPercent || 0} %</Td>
                                  <Td>
                                    {currency?.symbol || "₹"}{" "}
                                    {formatAmountIndianRupees(
                                      item?.totalTDS || 0
                                    )}
                                  </Td>
                                </Tr>
                              );
                            })}
                            <Tr>
                              <Td className="f-600" colSpan={2}>
                                Total TDS:
                              </Td>
                              <Td className="f-600">
                                <span>{currency?.symbol || "₹"} </span>
                                {formatAmountIndianRupees(calculateTotalTDS())}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </div>
                    )}
                    {creditData && creditData?.length ? (
                      <div className="payment-table-box">
                        <h4 className="heading">Credit Information</h4>
                        <Table>
                          <Thead>
                            <Tr>
                              <Th>CR No.</Th>
                              <Th>Amount</Th>
                              <Th>Use Amount</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {creditData.map((item, index) => {
                              return (
                                <Tr>
                                  <Td>{poNumberFormate(item?.crId.crNo)}</Td>
                                  <Td>
                                  {currency?.symbol || "₹" } {formatAmountIndianRupees(item?.crId?.amt || 0)}
                                  </Td>
                                  <Td>{currency?.symbol || "₹" } {formatAmountIndianRupees(item?.usAmt || 0)}</Td>
                                </Tr>
                              );
                            })}
                            <Tr>
                              <Td className="f-600" colSpan={2}>
                                Total Credit:
                              </Td>
                              <Td className="f-600">
                              {currency?.symbol || "₹" }{" "}
                                {formatAmountIndianRupees(calculateCreditAmt())}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </div>
                    ) : (
                      ""
                    )}
                    {historyData && historyData?.length > 0 && (
                      <div className="payment-table-box">
                        <h4 className="heading">Payment History</h4>
                        <Table>
                          <Thead>
                            <Tr>
                              <Th>Payment ID</Th>
                              <Th>Payment Date</Th>
                              <Th>Payment Mode</Th>
                              <Th>Received Amount</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {historyData?.map((item) => {
                              return (
                                <Tr>
                                  <Td>
                                    {item?.pyNo
                                      ? pmtNumberFormate(item?.pyNo)
                                      : "-"}
                                  </Td>
                                  <Td>{dateTimeFormat(expenseData?.dt)}</Td>

                                  <Td>{getPaymentMode(item?.mod)}</Td>
                                  <Td>
                                    {currency?.symbol || "₹"}{" "}
                                    {formatAmountIndianRupees(item?.amt || 0)}
                                  </Td>
                                </Tr>
                              );
                            })}
                            <Tr>
                              <Td className="f-600" colSpan={3}>
                                Total Payment:
                              </Td>
                              <Td className="f-600">
                                <span>{currency?.symbol || "₹"} </span>
                                {formatAmountIndianRupees(
                                  calculateTotalPaymentHistory()
                                )}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </div>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PaymentReceiptPdf;
